import React, { useState, useCallback, useEffect, useRef } from "react";
import { withFormik } from 'formik';
import * as Yup from "yup";
import moment from "moment"
import { useTranslation } from 'react-i18next';
import { useStoreState, useStoreActions } from 'easy-peasy';

// Storybook
import { Button, Typography, Spinner, IconButton } from "stories/components";
import { Container, Row, Col, FormContainer, Card, CardBody } from "stories/layout";
import { Input, SelectMulti, Toggle, Radio, DatePicker, Checkbox } from "stories/forms";

import { useNotification } from "stories/components/Notification"

// App
import { round, roundScaleDecimals, DERIVATIVE_PRICE_DECIMALS, padDerivativePrice } from "services/helpers"

import DerivativeTypeInput from "components/Forms/DerivativeTypeInput/DerivativeTypeInput"
import OptionRightInput from "components/Forms/OptionRightInput/OptionRightInput"
import OptionAssetTypeInput from "components/Forms/OptionAssetTypeInput/OptionAssetTypeInput"
import OptionRoleInput from "components/Forms/OptionRoleInput/OptionRoleInput"
import OptionStateInput from "components/Forms/OptionStateInput/OptionStateInput"
import StockSelect from "components/Forms/StockSelect/StockSelect"
import StockPrivateSelect from "components/Forms/StockPrivateSelect/StockPrivateSelect"
import StockMarketSelect from "components/Forms/StockMarketSelect/StockMarketSelect"
import PrivateInvestmentFundSelect from "components/Forms/PrivateInvestmentFundSelect/PrivateInvestmentFundSelect"
import CurrencySelect from "components/Forms/CurrencySelect/CurrencySelect"
import UpdateUp from "views/vismapay/UpdateUp/UpdateUp"

import HelpText from "components/Info/HelpText.js"
import ImageWithPolygons from 'components/ImageWithPolygons/ImageWithPolygons';
import PdfWithPolygons from "components/PdfWithPolygons/PdfWithPolygons";

import TransactionsFileHandling from "components/Forms/TransactionsFileHandling/TransactionsFileHandling"
import {
  httpUploadFiles,
  httpCreateFileAiMessages
} from "services/files"
import UploadFile from "components/Forms/UploadFile/UploadFile"

import AllSecuritySelect2 from "components/Forms/AllSecuritySelect/AllSecuritySelect2"
import DerivativeTerminationContractCard from "views/app/OptionEdit/DerivativeTerminationContractCard"
import TerminationSettlementFields from "views/app/OptionEdit/TerminationSettlementFields"
import { getPreOptionLinkId, isPartialTerminationPair } from "views/app/PreOptionEdit/groupPreOptions"
import { buildPartialTerminationApproval } from "views/app/PreOptionEdit/partialTerminationApproval"
import { findOptionForReferences } from "views/app/PreOptionEdit/tradeIdMatch"
import { getContractQuantity, resolveLegacyTerminationSettlement } from "views/app/OptionEdit/derivativeTerminationUtils"
import { getOptionStateLabel } from "components/Forms/OptionStateInput/optionStateLabels"

import { 
  httpGetOption,
  httpGetOptions,
  httpCreateOption, 
  httpUpdateOption, 
  httpDeleteOption,
  httpUpdatePreOption
} from "services/options"

import { httpGetCurrencyRate } from "services/currencies"

const getLinkedOptionId = (obj) => {
  if (obj?.linked_option_data?.id) return obj.linked_option_data.id;
  if (!obj?.linked_option) return null;
  return typeof obj.linked_option === "object" ? obj.linked_option.id : obj.linked_option;
};

const extractOptionsList = (payload) => {
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload)) return payload;
  return [];
};

let today_date = new Date()
today_date.setHours(12, 0, 0, 0);

const defaultValues = {
  id: null,
  derivative_type: 2,
  asset_type: 2,
  option_right: "2",
  spot_price_manual: 0.0,
  strike_price: padDerivativePrice(0),
  lot_size: 1,
  lot_amount: 1,
  currency_1: "EUR",
  currency_2: "USD",
  exercise_price: padDerivativePrice(0),
  start_date: today_date,
  end_date: today_date,
  owner_name: "",
  writer_name: "",
  option_state: 2,
  option_state_date: null,
  termination_price: padDerivativePrice(0),
  termination_fee: 0.0,
  receives: 0.0,
  premium: padDerivativePrice(0),
  premium_total: padDerivativePrice(0),
  other_expenses: padDerivativePrice(0),
}

const DECIMAL_PLACES = 6;
const PRICE_DECIMAL_PLACES = DERIVATIVE_PRICE_DECIMALS;

const PreOptionEdit = (props) => {
  
  const {
     values,
     touched,
     setTouched,
     errors,
     setErrors,
     handleChange,
     handleBlur,
     handleSubmit,
     setFieldValue,
     setFieldError,
     setFieldTouched,
     isValid,
     isTouched,
     isSubmitting,
     validateForm,
     preSetValues,
     objectId,
     setValues,
   } = props;
   
   const {
      t,
      history,
      stockMarket,
      setStockMarket,
      getBasicDataObjects,
      portfolioSourceRate,
      setPortfolioSourceRate,
      securitySourceRateDate,
      setSecuritySourceRateDate,
      stocks,
      setFiles,
      files,
      handleFileUpload,
      trxFiles,
      setTrxFiles,
      object,
      portfolios,
      portfolio,
      linkedOption,
      linkedOptionStockMarket,
      portfolioOptions,
      onLinkedOptionChange,
 } = props;
   
 const getFormikInputProps = useCallback(
    name => ({
      name,
      value: values[name],
      error: Boolean(touched[name] && errors[name]),
      errorText: touched[name] ? errors[name] : "",
      onBlur: handleBlur,
      onChange: handleChange
    }),
    [values, errors, touched, handleBlur, handleChange]
  );
  
  const account = useStoreState((state) => state.account?.account);

  const remainingQuantity = parseInt(values?.quantity, 10) || 0;
  const originalQuantity = linkedOption ? getContractQuantity(linkedOption) : remainingQuantity;
  const terminationPre = object?.terminationPreOption
    || (typeof object?.linked_pre_option === "object" ? object.linked_pre_option : null);
  const isTerminationReview = isPartialTerminationPair(object)
    || object?.derivative_action === "partial_terminate"
    || object?.derivative_action === "full_terminate"
    || Number(values?.option_state) === 5
    || (linkedOption && originalQuantity > remainingQuantity);
  const showTerminationSettlement = isTerminationReview;
  const terminatedQuantity = isPartialTerminationPair(object) && linkedOption
    ? Math.max(originalQuantity - remainingQuantity, 0)
    : (parseInt(terminationPre?.quantity, 10) || remainingQuantity);

  const derivativeChoiceLabel = (opt) => {
    const trade = opt?.trade_id || (opt?.id != null ? `#${opt.id}` : "");
    const name = opt?.asset_name || "";
    const quantity = opt?.quantity != null && opt?.quantity !== "" ? String(opt.quantity) : "";
    const state = getOptionStateLabel(t, opt?.option_state);
    return [trade, name, quantity, state].filter(Boolean).join(" · ");
  };

  const derivativeChoices = [...(portfolioOptions || [])]
    .filter((opt) => opt?.id != null && Number(opt.option_state) === 2)
    .sort((a, b) => String(a?.trade_id || "").localeCompare(String(b?.trade_id || "")))
    .map((opt) => ({ value: opt.id, label: derivativeChoiceLabel(opt) }));

  const selectedDerivativeChoice = linkedOption?.id != null
    ? {
        value: linkedOption.id,
        label: derivativeChoices.find((choice) => Number(choice.value) === Number(linkedOption.id))?.label
          || derivativeChoiceLabel(linkedOption),
      }
    : null;

  const handleDerivativeSelect = (_name, selected) => {
    const id = selected?.id ?? selected?.value;
    const option = (portfolioOptions || []).find((opt) => Number(opt.id) === Number(id));
    if (!option || Number(option.option_state) !== 2) return;
    setFieldValue("id", option.id);
    onLinkedOptionChange?.(option);
  };
  
  const [price_rate, setPriceRate] = useState(null);
  const [price_rate_loading, setPriceRateLoading] = useState(false);
  
  const getPrivateBasicDataObjects = useStoreActions((actions) => actions.privateStocks.getObjects);
  
  const privateStocks = useStoreState((state) => state.privateStocks?.objects);
  const private_investment_funds = useStoreState((state) => state.privateInvestmentFunds?.objects);

  const onSubmit = (e) => {
    e.preventDefault();
    validateForm().then(res => {
      if (Object.keys(res).length === 0) {
        handleSubmit()
      }
      else {
        setTouched(res);
        setErrors({
          ...res,
          general: t("Please check the form errors", "Tarkista lomakkeen virheet")
        })
      }
    })
  }

  const handlePremiumChange = ({target}, child) => {
    const premium = target.value.toString().replace(",",".")
    setFieldValue("premium", premium)
    // Only calculate premium_total if it's not already provided from PreOption data
    if (values?.quantity && (!object?.premium_total || object?.premium_total === 0)) {
      const premium_total = round(parseFloat(premium) * parseInt(values?.quantity), PRICE_DECIMAL_PLACES)
      setFieldValue("premium_total", padDerivativePrice(premium_total))
    }
  }
  
  const handlePremiumTotalChange = ({target}, child) => {
    const premium_total = target.value.toString().replace(",",".")
    setFieldValue("premium_total", premium_total)
    if (values?.quantity) {
      const premium = round(parseFloat(premium_total) / parseInt(values?.quantity), PRICE_DECIMAL_PLACES)
      setFieldValue("premium", padDerivativePrice(premium))
    }
  }
  
  useEffect(() => {
    const lot_size = parseInt(values?.lot_size)
    const lot_amount = parseInt(values?.lot_amount)
    const quantity = lot_size * lot_amount
    setFieldValue("quantity", quantity)
  }, [values?.lot_size, values?.lot_amount])
  
  useEffect(() => {
    // Only calculate premium_total if it's not already provided from PreOption data
    if (values?.quantity && values?.premium && (!object?.premium_total || object?.premium_total === 0))  {
      const premium_total = parseInt(values?.quantity) * parseFloat(values?.premium)
      setFieldValue("premium_total", padDerivativePrice(premium_total))
    }
  }, [values?.quantity, object?.premium_total])

  useEffect(() => {
    if (!portfolio?.currency_label) return;
    setFieldValue("currency_1", portfolio.currency_label);
  }, [portfolio])

  // ------------------CALCULATE TERMINATION FEE-----------------------------------
  // useEffect(() => {
  //   if (values?.termination_price && values?.strike_price) {
  //     const termPrice = parseFloat(values?.termination_price) || 0;
  //     const strikePrice = parseFloat(values?.strike_price) || 0;
  //     let terminationFee = strikePrice - termPrice;
      
  //     // If role is seller (3), multiply by -1
  //     if (values?.role == 3 || values?.role == "3") {
  //       terminationFee = terminationFee * -1;
  //     }
      
  //     setFieldValue("termination_fee", round(terminationFee, DECIMAL_PLACES));
  //   }
  // }, [values?.termination_price, values?.strike_price, values?.role])

  // ------------------UPDATE CURRENCY DATE-----------------------------------
  useEffect(() => {
    if (values?.currency_1 && values?.start_date) {
      
      if (values?.currency_1 === "EUR") {
        setPortfolioSourceRate(1.0)
      }
      else {
        // Use AI-provided exchange rate if available, otherwise fallback to API
        if (values?.currency_exchange_rate && values?.currency_exchange_rate !== 1) {
          setPortfolioSourceRate(values?.currency_exchange_rate)
        } else {
          let params = {
            offset:0, 
            limit:1, 
          }
          params["Date"] = moment(values?.start_date).format("YYYY-MM-DD")
          params['currency'] = values?.currency_1
          httpGetCurrencyRate(params).then(res => {
            if (res?.data?.currency_rate) {
              setPortfolioSourceRate(res?.data?.currency_rate)
            }
            else {
              setPortfolioSourceRate(1.0)
            }
          }, err => {
            setPortfolioSourceRate(null)
          })
        }
      }
    }
    else {
      setPortfolioSourceRate(null)
    }
  }, [values?.currency_1, values?.start_date, values?.currency_exchange_rate])

  const resolveStockPublic = (params) => {
    if (params?.name) {
      setFieldValue("asset_name", params?.name)
    }
  }
  
  const resolveStockPrivate = (params) => {
    if (params?.name) {
      setFieldValue("asset_name", params?.name)
    }
  }
  
  const resolvePrivateInvestmentFund = (params) => {
    if (params?.name) {
      setFieldValue("asset_name", params?.name)
    }
  }

  const handleCancel = () => {
    props.handleReject()
  }
  
  const handleConfirm = () => {
    /*
    httpConfirmPurchaseOrderShipping(values?.id).then(res => {
      handleSubmit()
    })
    */
  }
  
  const handleStockMarketChange = ({target}) => {
    setStockMarket(target.value)
    setFieldValue("asset_id", null);
    setFieldValue("asset_name", "");
  }
  
  useEffect(() => {
    getBasicDataObjects({stock_market:stockMarket})
  }, [stockMarket])
  
  useEffect(() => {
    if (!objectId){
      setFieldValue("asset_id", null);
      setFieldValue("asset_name", "");
    }
  }, [values?.asset_type]);

  const handlePass = () => {
    const pre_update_data ={
        id: object?.id,
        portfolio: object?.portfolio,
        processed: true,
        review_status: "skipped",
    }
    const linkedPreOptionId = getPreOptionLinkId(object?.linked_pre_option)
      || object?.terminationPreOption?.id;
    const markProcessed = httpUpdatePreOption(pre_update_data).then((res) => {
      if (!linkedPreOptionId || linkedPreOptionId === pre_update_data.id) return res;
      return httpUpdatePreOption({
        id: linkedPreOptionId,
        portfolio: object?.portfolio,
        processed: true,
        review_status: "skipped",
      }).then(() => res, () => res);
    });
    markProcessed.then(res => {
        console.log("httpUpdatePreOption", res)
        props.handleResolve()
    }, error => {
        console.log("httpUpdatePreOption error", error)
        props.handleResolve()
    }
    )
  }

  const getAssetTypeSelect = useCallback(() => {
    // Apufunktio joka hakee arvon oikeasta listasta
    const getAssetValue = () => {
      if (!values?.asset_id) return null;
      
      let asset = null;
      if (values?.asset_type == 2) {
        asset = stocks?.find(s => s.id === values.asset_id);
      } 
      else if (values?.asset_type == 6) {
        asset = privateStocks?.find(s => s.id === values.asset_id);
      }
      else if (values?.asset_type == 10) {
        asset = private_investment_funds?.find(s => s.id === values.asset_id);
      }

      if (!asset) return null;
      return {
        value: asset.id,
        label: asset.name,
      };
    };

    if (values?.asset_type == 2) {
      return (
        <>
          <StockMarketSelect 
            onChange={handleStockMarketChange} 
            value={stockMarket} 
            showPrivate={false} 
            className={`${values?.asset_name_polygon?.length > 0? "border rounded border-palet-yellow":"" }`}
          />
          <StockSelect
            {...getFormikInputProps("asset_id")}
            value={getAssetValue()}
            label={t("option_edit_asset_stock", "Stock")}
            setFieldValue={setFieldValue}
            setFieldValueReturnId={true}
            handleResolve={resolveStockPublic}
            required={true}
            className={`${values?.asset_name_polygon?.length > 0? "border rounded border-palet-yellow":"" }`}
          />
        </>
      )
    }
    else if (values?.asset_type == 6) {
      return (
        <StockPrivateSelect
          {...getFormikInputProps("asset_id")}
          label={t("option_edit_private_stock", "Stock")}
          setFieldValue={setFieldValue}
          handleResolve={resolveStockPrivate}
          className={`${values?.asset_name_polygon?.length > 0? "border rounded border-palet-yellow":"" }`}
        />
      )
    }
    else if (values?.asset_type == 10) {
      return (
        
        <PrivateInvestmentFundSelect
          {...getFormikInputProps("asset_id")}
          value={getAssetValue()}
          label={t("option_edit_private_investment_fund", "Investment fund")}
          setFieldValue={setFieldValue}
          handleResolve={resolvePrivateInvestmentFund}
          className={`${values?.asset_name_polygon?.length > 0? "border rounded border-palet-yellow":"" }`}
          />
      )
    }
    else if (values?.asset_type == 12) {
      return (
        <>
          <CurrencySelect
            {...getFormikInputProps("currency_1")} 
            label={t("option_edit_currency_1", "Currency")}
            required
          />
          <CurrencySelect
            {...getFormikInputProps("currency_2")} 
            label={t("option_edit_currency_2", "Currency")}
            required
          />
        </>
      )
    }
    else {
      return null;// t("No data available","Ei dataa saatavilla")
    }
  }, [values?.asset_type, stockMarket, values?.asset_id, stocks, privateStocks, private_investment_funds, values?.asset_name_polygon])
  
  useEffect(() => {
    if (values?.asset_type == 12) {
      setFieldValue("asset_name", `${values?.currency_1}-${values?.currency_2}`)
    }
    
  }, [values?.asset_type, values?.currency_1, values?.currency_2])

  // Ensure asset_name is set when object data is available
  useEffect(() => {
    if (object?.asset_name && !values?.asset_name) {
      setFieldValue("asset_name", object?.asset_name)
    } else if (object?.related_model?.name && !values?.asset_name) {
      setFieldValue("asset_name", object?.related_model?.name)
    }
  }, [object, values?.asset_name])

  const allSecurity2OnChange = (value) => {
    // Convert related_model_type to asset_type
    let asset_type = value?.related_model_type;
    if (value?.related_model_type === 10) {
      asset_type = 2; // Public stock
    } else if (value?.related_model_type === 20) {
      asset_type = 6; // Private stock  
    } else if (value?.related_model_type === 30) {
      asset_type = 10; // Private investment fund
    }
 
    
    setFieldValue("asset_type", asset_type)
    setFieldValue("related_model_type", value?.related_model_type)
    setStockMarket(value?.stock_market)
    setFieldValue("asset_id", value?.value)
    setFieldValue("related_model_id", value?.value)
    setFieldValue("asset_name", value?.name)
    setFieldValue("asset_stock", {id: value?.value, value: value?.value, label: value?.name, ticker_symbol: value?.ticker_symbol, stock_market: value?.stock_market, currency_label: value?.currency_label})
  }

  const get_initial_page = (values) => {
    //loop polygons and find smallest page, but if hover is true, then return that page
    let page = 1
    let smallest_page = 0
    let hover = false
    values.map(obj => {
      if (smallest_page === 0 && obj.page > 0) {
        smallest_page = obj.page -1
      }
      else if (obj.page <= smallest_page && obj.page > 0) {
        smallest_page = obj.page -1
      }
    })
    
    return smallest_page 
  }

  const getImageWithPolygon = useCallback(() => {
    // console.log("trxFiles",trxFiles)
    //if trxFiles is not empty and file is pdf
    let polygons=[
      {polygon: values?.asset_name_polygon, page: values?.asset_name_page},
      {polygon: values?.strike_price_polygon, page: values?.strike_price_page},
      {polygon: values?.premium_polygon, page: values?.premium_page},
      {polygon: values?.premium_total_polygon, page: values?.premium_total_page},
      {polygon: values?.other_expenses_polygon, page: values?.other_expenses_page},
      {polygon: values?.start_date_polygon, page: values?.start_date_page},
      {polygon: values?.end_date_polygon, page: values?.end_date_page},
      {polygon: values?.lot_size_polygon, page: values?.lot_size_page},
      {polygon: values?.lot_amount_polygon, page: values?.lot_amount_page},
      {polygon: values?.broker_polygon, page: values?.broker_page},
      {polygon: values?.reference_code_polygon, page: values?.reference_code_page},
      {polygon: values?.owner_name_polygon, page: values?.owner_name_page},
      {polygon: values?.writer_name_polygon, page: values?.writer_name_page},
      {polygon: values?.option_state_date_polygon, page: values?.option_state_date_page},
      {polygon: values?.termination_price_polygon, page: values?.termination_price_page},
    ]
    
    // Define custom colors matching the form field colors
    const customColors = [
      'rgba(253, 205, 63, 0.9)',   // 0: asset_name - Yellow
      'rgba(22, 22, 64, 0.9)',     // 1: strike_price - Dark Blue
      'rgba(255, 111, 97, 0.9)',   // 2: premium - Coral
      'rgba(107, 91, 149, 0.9)',   // 3: premium_total - Purple
      'rgba(229, 57, 53, 0.9)',    // 4: other_expenses - Red (MiFID costs total)
      'rgba(107, 91, 149, 0.9)',   // 5: start_date - Purple
      'rgba(247, 202, 201, 0.9)',  // 6: end_date - Light Pink
      'rgba(146, 168, 209, 0.9)',  // 7: lot_size - Light Blue
      'rgba(149, 82, 81, 0.9)',    // 8: lot_amount - Brownish Red
      'rgba(181, 101, 167, 0.9)',  // 9: broker - Lavender
      'rgba(255, 160, 122, 0.9)',  // 10: reference_code - Light Salmon
      'rgba(72, 201, 176, 0.9)',   // 11: owner_name - Mint Green
      'rgba(255, 195, 0, 0.9)',    // 12: writer_name - Bright Yellow
      'rgba(253, 205, 63, 0.9)',   // 13: option_state_date - Yellow
      'rgba(22, 22, 64, 0.9)',     // 14: termination_price - Dark Blue
    ];

    const file = (trxFiles || []).find((item) => item && item.filename);
    if (!file) {
      return null;
    }

    if (file.filename.includes(".pdf")) {
      return (
        <PdfWithPolygons 
          fileId={file.id}  
          fileName={file.filename}  
          polygons={polygons}
          colors={customColors}
          initialPage={get_initial_page(polygons)}
          setFieldValue={setFieldValue} /> 
      )
    }

    return(
      <ImageWithPolygons 
        fileId={file.id}  
        fileName={file.filename}  
        polygons={[
          values?.asset_name_polygon,
          values?.strike_price_polygon,
          values?.premium_polygon,
          values?.premium_total_polygon,
          values?.other_expenses_polygon,
          values?.start_date_polygon,
          values?.end_date_polygon,
          values?.lot_size_polygon,
          values?.lot_amount_polygon,
          values?.broker_polygon,
          values?.reference_code_polygon,
          values?.owner_name_polygon,
          values?.writer_name_polygon,
          values?.option_state_date_polygon,
          values?.termination_price_polygon,
        ]} 
        setFieldValue={setFieldValue} /> 
    )
  }, [trxFiles, values])
  
  return (
    <>
    
    <div className="px-1">
     
      <Row className="mt-4 mb-1 border-bottom ">
        <Col xs={12} md={6} className="ml-auto pb-3">
          {isPartialTerminationPair(object) ? (
            <div className="d-inline-flex">
              <Typography variant="h2">{t("pre_option_partial_title", "Osittainen terminointi")}: </Typography>
              <Typography variant="h2" className="font-weight-light ml-1">
                {portfolios?.find(p => p.id === values?.portfolio)?.name || portfolio?.name}
              </Typography>
            </div>
          ) : values?.id ? (
            <div className="d-inline-flex">
              <Typography variant="h2">{t("pre_option_update_existing", "Päivitetään olemassa oleva kirjaus")} #{values?.id}: </Typography>
              <Typography variant="h2" className="font-weight-light ml-1">
                {portfolios?.find(p => p.id === values?.portfolio)?.name || portfolio?.name}
              </Typography>
            </div>
          ) : objectId ? (
            <Typography variant="h2">{t("pre_option_edit_title", "Muokkaa johdannaista")}</Typography>
          ) : (
            <div className="d-inline-flex">
              <Typography variant="h2">{t("pre_option_add_title", "Lisää johdannainen salkkuun")}: </Typography>
              <Typography variant="h2" className="font-weight-light ml-1">
                {portfolios?.find(p => p.id === values?.portfolio)?.name || portfolio?.name}
              </Typography>
            </div>
          )}
        </Col>
        <Col xs={12} md={6} className="ml-auto pb-3 d-flex justify-content-end align-items-center">
          <IconButton
            aria-label={t("Close", "Sulje")}
            size="lg"
            onClick={handleCancel}
            iconName="times"
            data-dismiss="modal"
          />
        </Col>
      </Row>

      {linkedOption && (
        <DerivativeTerminationContractCard
          title={t("pre_option_original_contract", "Terminoitava sopimus")}
          afterTitle={linkedOption ? (
            <SelectMulti
              isMulti={false}
              setValueToSelect
              name="linked_derivative"
              placeholder={t("pre_option_select_derivative_placeholder", "Valitse avoin johdannainen")}
              options={derivativeChoices}
              value={selectedDerivativeChoice}
              setFieldValue={handleDerivativeSelect}
            />
          ) : null}
          contract={linkedOption}
          stockMarket={linkedOptionStockMarket}
          showStateDate={showTerminationSettlement}
          stateDate={terminationPre?.option_state_date || values?.option_state_date}
          settlement={showTerminationSettlement ? {
            quantity: terminatedQuantity,
            unitPrice: values?.termination_price,
            onUnitPriceChange: (value) => {
              setFieldValue("termination_price", value);
              setFieldValue("termination_fee", value);
            },
            total: values?.receives,
            onTotalChange: (value) => setFieldValue("receives", value),
            strikePrice: linkedOption?.strike_price ?? values?.strike_price,
            unitClassName: values?.termination_price_polygon?.length > 0 ? "border border-palet-dark-blue" : undefined,
          } : undefined}
        />
      )}
      {showTerminationSettlement && !linkedOption && (
        <TerminationSettlementFields
          quantity={terminatedQuantity}
          stateDate={terminationPre?.option_state_date || values?.option_state_date}
          unitPrice={values?.termination_price}
          onUnitPriceChange={(value) => {
            setFieldValue("termination_price", value);
            setFieldValue("termination_fee", value);
          }}
          total={values?.receives}
          onTotalChange={(value) => setFieldValue("receives", value)}
          strikePrice={values?.strike_price}
          role={values?.role}
          optionRight={values?.option_right}
          derivativeType={values?.derivative_type}
          unitClassName={values?.termination_price_polygon?.length > 0 ? "border border-palet-dark-blue" : undefined}
        />
      )}
      {linkedOption && (
        <Typography variant="h4" className="mb-3 mt-3">
          {isPartialTerminationPair(object)
            ? t("pre_option_review_remaining", "Uusi sopimus (jäljelle jäävä)")
            : t("pre_option_review_extracted", "Tarkistettava kirjaus")}
        </Typography>
      )}
      
      <Row  className="py-3 ">
        <Col xs={12} lg={6} xl={6}  className="border-right px-1 pr-2 pt-0">

          <Row>
            <Col className="mb-3">
              <DerivativeTypeInput
                {...getFormikInputProps("derivative_type")}
                label={t("option_edit_derivative_type", "Derivative type")}
                allOption={false}
              />
            </Col>
          </Row>
        
          {/* 
          <Row>
            <Col className="mb-3">
              <OptionAssetTypeInput
                {...getFormikInputProps("asset_type")}
                label={t("option_edit_asset_type", "Asset type")}
                allOption={false}
              />
            </Col>
          </Row>

          <Row>
            <Col className="mb-3">
            { getAssetTypeSelect() }
            </Col>
          </Row>
          */}

          <Row>
            <Col className="mb-3">
              <AllSecuritySelect2  
                label={t("security", "Arvopaperi")} 
                portfolio={values?.portfolio} 
                onChange={allSecurity2OnChange} 
                delisted={true} 
                value={values?.asset_stock} 
                className={`${values?.asset_name_polygon?.length > 0? "border rounded border-palet-yellow":"" }`} 
              />
            </Col>
          </Row>
          
          <Row>
            <Col className="mb-3">
              <Input
                {...getFormikInputProps("asset_name")}
                label={t("option_edit_name", "Name")}
                required
                className={`${values?.asset_name_polygon?.length > 0? "border border-palet-yellow":"" }`}
              />
            </Col>
          </Row>
          
          {values?.derivative_type == 2 && 
            <Row>
            <Col className="mb-3">
              <OptionRightInput
                {...getFormikInputProps("option_right")}
                label={t("option_edit_right", "Right")}
                allOption={false}
                />
            </Col>
          </Row>
          }
          
          <Row>
            <Col className="mb-3">
              <Input
                {...getFormikInputProps("owner_name")}
                label={t("option_edit_owner", "Owner/Buyer")}
                required
                className={`${values?.owner_name_polygon?.length > 0? "border border-palet-mint-green":"" }`}
              />
            </Col>
          </Row>
          
          <Row>
            <Col className="mb-3">
              <Input
                {...getFormikInputProps("writer_name")}
                label={t("option_edit_writer", "Writer/Seller")}
                required
                className={`${values?.writer_name_polygon?.length > 0? "border border-palet-bright-yellow":"" }`}
              />
            </Col>
          </Row>
          
          <Row>
            <Col className="mb-3">
              <OptionRoleInput
                {...getFormikInputProps("role")}
                label={t("option_edit_role", "Role")}
                allOption={false}
              />
            </Col>
          </Row>

          <Row className="mt-2">
            <Col sm={12} lg={12} className="d-flex align-items-center">
              <label className="mb-0 pr-1 header">{t("broker", "Välittäjä")}</label>
            </Col>
            <Col className="mb-0">
              <Input
                {...getFormikInputProps("broker_name")}
                placeholder={t("broker_placeholder", "Esim. Nordea")}
                className={`${values?.broker_polygon?.length > 0? "border border-palet-lavender":"" }`}
              />
            </Col>
          </Row>
          
          <Row className="mb-3 mt-3">
            <Col sm={12} lg={12} className="d-flex align-items-center">
              <label className="mb-0 pr-1 header">{t("reference_code", "Viite")}</label>
            </Col>
            <Col className="mb-0">
              <Input
                {...getFormikInputProps("reference_code")}
                className={`${values?.reference_code_polygon?.length > 0? "border border-palet-light-salmon":"" }`}
              />
            </Col>
          </Row>

          <Row className="mb-3">
            <Col sm={12} lg={12} className="d-flex align-items-center">
              <label className="mb-1 pr-1 header">{t("option_edit_notes", "Kommentit")}</label>
            </Col>
            <Col >
              <Input
                {...getFormikInputProps("notes")}
                label=""
                type="textarea"
              />
            </Col>
          </Row>

        </Col>
        
        <Col sm={12} lg={6} xl={3} className="px-2 pt-0">
          <Row>
            <Col className="mb-0">
               <label className="mb-1 pr-1 header-bold">{t("option_information", "Johdannaistiedot")}</label>
            </Col>
          </Row>

          <Row>
            <Col className="mb-3">
              <Input
                {...getFormikInputProps("lot_size")}
                label={t("option_edit_lot_size", "Lot size")}
                type="number"
                min={1}
                className={`${values?.lot_size_polygon?.length > 0? "border border-palet-light-blue":"" }`}
              />
            </Col>
          </Row>

          <Row>
            <Col className="mb-3">
              <Input
                {...getFormikInputProps("lot_amount")}
                label={t("option_edit_lot_amount", "Lots amount")}
                type="number"
                min={1}
                className={`${values?.lot_amount_polygon?.length > 0? "border border-palet-brown-ishred":"" }`}
              />
            </Col>
          </Row>

          <Row>
            <Col className="mb-3">
              <Input
                {...getFormikInputProps("quantity")}
                label={t("option_edit_quantity", "Quantity")}
                type="number"
                min={0}
              />
            </Col>
          </Row>
          
          <Row>
            <Col className="mb-3">
              <DatePicker
                {...getFormikInputProps("start_date")}
                label={t("option_edit_start_date", "Start date")}
                className={`${values?.start_date_polygon?.length > 0? "border border-palet-purple form-control":"form-control" }`}
                selected={values?.start_date}
                name="start_date"
                onChange={(date) => setFieldValue("start_date", date)}
                showYearDropdown
                dateFormat="d.M.yyyy"
                value={values?.start_date}
                required
                customInput={<Input addonTypeClass="append" iconColor={"bg-light"} inputText={values?.start_date ? (moment(values?.start_date).format("dddd")):""}/>}
              />
            </Col>
          </Row>
          
          <Row>
            <Col className="mb-3">
              <DatePicker
                {...getFormikInputProps("end_date")}
                label={t("option_edit_end_date", "Expiry date")}
                className={`${values?.end_date_polygon?.length > 0? "border border-palet-light-pink form-control":"form-control" }`}
                selected={values?.end_date}
                name="end_date"
                onChange={(date) => setFieldValue("end_date", date)}
                showYearDropdown
                dateFormat="d.M.yyyy"
                value={values?.end_date}
                required
                customInput={<Input addonTypeClass="append" iconColor={"bg-light"} inputText={values?.end_date ? (moment(values?.end_date).format("dddd")):""}/>}
              />
            </Col>
          </Row>
          
          <Row>
            <Col className="mb-3">
              <Input
                {...getFormikInputProps("premium")}
                onChange={handlePremiumChange}
                label={t("option_edit_premium", "Premium / share")}
                type="decimal"
                decimalPlaces={PRICE_DECIMAL_PLACES}
                className={`${values?.premium_polygon?.length > 0? "border border-palet-coral":"" }`}
              />
            </Col>
          </Row>
          
          <Row>
            <Col className="mb-3">
              <Input
                {...getFormikInputProps("premium_total")}
                onChange={handlePremiumTotalChange}
                label={t("option_edit_premium_total", "Premium total")}
                type="decimal"
                decimalPlaces={PRICE_DECIMAL_PLACES}
                className={`${values?.premium_total_polygon?.length > 0? "border border-palet-purple":"" }`}
              />
            </Col>
          </Row>
          
          <Row>
            <Col className="mb-3">
              <Input
                {...getFormikInputProps("other_expenses")}
                label={t("option_edit_other_expenses", "Muut kulut")}
                type="decimal"
                decimalPlaces={PRICE_DECIMAL_PLACES}
                className={`${values?.other_expenses_polygon?.length > 0 ? "border border-danger" : ""}`}
              />
            </Col>
          </Row>
          
          <Row>
            <Col className="mb-3">
              <Input
                {...getFormikInputProps("strike_price")}
                label={t("option_edit_strike_price", "Strike price")}
                type="decimal"
                decimalPlaces={PRICE_DECIMAL_PLACES}
                className={`${values?.strike_price_polygon?.length > 0? "border border-palet-dark-blue":"" }`}
              />
            </Col>
          </Row>
          
          <Row>
            <Col className="mb-3">
              <OptionStateInput
                {...getFormikInputProps("option_state")}
                label={t("option_edit_state", "State")}
                allOption={false}
              />
            </Col>
          </Row>
          
          { values?.option_state && values?.option_state > 2 ? (
            <Row>
              <Col className="mb-3">
                <label className="mb-1 pr-1 header-bold">{t("option_edit_state_date", "State date")}</label>
                <DatePicker
                  className={`${values?.option_state_date_polygon?.length > 0? "border border-palet-yellow form-control":"form-control" }`}
                  selected={values?.option_state_date}
                  name="option_state_date"
                  onChange={(date) => setFieldValue("option_state_date", date)}
                  showYearDropdown
                  dateFormat="d.M.yyyy"
                  value={values?.option_state_date}
                  customInput={<Input addonTypeClass="append" iconColor={"bg-light"} inputText={values?.option_state_date ? (moment(values?.option_state_date).format("dddd")):""}/>}
                />
              </Col>
            </Row>
          ) : null }
          
          { values?.option_state && (values?.option_state == 3 || values?.option_state == "3") ? (
            <Row>
              <Col className="mb-3">
                <Input
                  {...getFormikInputProps("exercise_price")}
                  label={t("option_edit_exercise_price", "Exercise price")}
                  type="decimal"
                  decimalPlaces={PRICE_DECIMAL_PLACES}
                />
              </Col>
            </Row>
          ) : null}
          

          {account && account?.license?.name !== "Lite" && (
            <>
            <Row className="mb-3">
            <Col sm={12} lg={12} className="d-flex ">
              <label className="mb-0 mt-2 pr-1 header">{t("attachment_file", "Liite")}</label>
            </Col>
            <Col className="mb-0">
              <UploadFile apiUrl="https://your-server.com/upload" setFiles={setFiles} files={files} padding={15} />
            </Col>
            
          </Row>
            <Row className="mb-3">
              <Col sm={12} lg={12} className="d-flex ">
              </Col>
              <Col className="mb-0">
                <TransactionsFileHandling setFiles={setTrxFiles} files={trxFiles} transaction={values} setFieldValue={setFieldValue} values={values}/>
              </Col>
              
            </Row>
            </>
            )}

          <Typography  variant="small" className="mt-2 text-gray">
            {t("purchase_edit_required","* Tähdellä merkityt ovat pakollisia")}
          </Typography>
        </Col>
        
        <Col xs={12} lg={12} xl={6} className="px-0 pt-0">
            {
              trxFiles?.length > 0 &&
              <div className="px-0">
                {getImageWithPolygon()}
              </div>
            }
        </Col>

      </Row>
      
      <Row>
        <Col>{/*JSON.stringify(values)*/}</Col>
      </Row>
      
      <Row className="  bg-lighter pt-3  rounded-bottom">
        <Col className="mb-3">
          <Button name='optionEdit_cancel' onClick={handleCancel} variant="secondary" className="sk-modal-dismiss">{t("Sulje")}</Button>
        </Col>
  
        <Col className="mb-3 text-center">
          <Button name='optionEdit_pass ' onClick={handlePass} variant="light">{t("Ohita")}</Button>
        </Col>
    
        <Col className="mb-3 text-right">
          { !preSetValues?.state_delivery && (
            <Button name='optionEdit_submit' variant="primary" disabled={isSubmitting} onClick={onSubmit}>
              {isPartialTerminationPair(object)
                ? t("pre_option_confirm_partial", "Hyväksy terminointi ja uusi sopimus")
                : t("Kirjaa ja tallenna")}
            </Button>
          )}
          
          { Boolean(errors["general"]) && <Typography className="text-danger">{errors["general"]}</Typography> }
        </Col>
      </Row>
      </div>
    </>
  );
}

const PreOptionEditFormik = withFormik({
    enableReinitialize: true,
    validateOnMount: true,
    mapPropsToValues: props => {
      const { preSetValues } = props;
      if ( preSetValues) {
        return preSetValues;
      } else {
        return {
          ...defaultValues
        }
      }
    },
    validationSchema: props => {
      const {t} = props;
      const required = t("validation_required_field","The field is required");
      return Yup.object().shape({
        asset_name: Yup.string().required(required).nullable(),
        end_date: Yup.string().required(required).nullable(),
        owner_name: Yup.string().required(required).nullable(),
        start_date: Yup.string().required(required).nullable(),
        writer_name: Yup.string().required(required).nullable(),
      });
    },
    handleSubmit: (values, { setSubmitting, setErrors, props }) => {
      const {t, portfolio, handleFileUpload, files, handleResolve, object, linkedOption} = props;
      
      const values_premium = values?.premium ? round(parseFloat(values?.premium.toString().replace(",",".")), PRICE_DECIMAL_PLACES) : 0;
      const values_premium_total = values?.premium_total ? round(parseFloat(values?.premium_total.toString().replace(",",".")), PRICE_DECIMAL_PLACES) : 0;
      const values_other_expenses = values?.other_expenses ? round(parseFloat(values?.other_expenses.toString().replace(",",".")), PRICE_DECIMAL_PLACES) : 0;
      const values_strike_price = values?.strike_price ? round(parseFloat(values?.strike_price.toString().replace(",",".")), PRICE_DECIMAL_PLACES) : 0;
      const values_termination_price = values?.termination_price ? round(parseFloat(values?.termination_price.toString().replace(",",".")), PRICE_DECIMAL_PLACES) : 0;
      const values_receives = values?.receives ? round(parseFloat(values?.receives.toString().replace(",",".")), PRICE_DECIMAL_PLACES) : 0;
      const values_termination_fee = values?.termination_fee
        ? round(parseFloat(values?.termination_fee.toString().replace(",",".")), DECIMAL_PLACES)
        : values_termination_price;

      //convert start_date and end_date to 12:00:00
      let start_date = values?.start_date;
      const oldStartDate = values?.start_date;
      const newStartDate = new Date(oldStartDate.getFullYear(), oldStartDate.getMonth(), oldStartDate.getDate(), 12, 0, 0);
      const formattedStartDate = newStartDate.toISOString().split('T')[0] +" 12:00:00Z";;
      start_date = formattedStartDate;

      let end_date = values?.end_date;
      const oldEndDate = values?.end_date;
      const newEndDate = new Date(oldEndDate.getFullYear(), oldEndDate.getMonth(), oldEndDate.getDate(), 12, 0, 0);
      const formattedEndDate = newEndDate.toISOString().split('T')[0] +" 12:00:00Z";;
      end_date = formattedEndDate;

      let connect_files =  []
      if (object?.file?.id) {
        connect_files.push({value: object.file.id, label: object.file.filename})
      }
      if (values?.connect_files){
        connect_files = connect_files.concat(values?.connect_files)
      }
      
      let data = {
        ...values,
        portfolio: portfolio?.id || object?.portfolio,
        start_date: start_date, 
        end_date: end_date,
        premium: values_premium,
        premium_total: values_premium_total,
        other_expenses: values_other_expenses,
        strike_price: values_strike_price,
        termination_price: values_termination_price,
        receives: values_receives,
        termination_fee: values_termination_fee,
        currency_exchange_rate: values?.currency_exchange_rate ? roundScaleDecimals(parseFloat(values?.currency_exchange_rate), 4) : 1,
        connect_files: connect_files,
        trade_id: values?.reference_code, // Convert reference_code to trade_id for Option model
      };
      
      // Remove reference_code since it's now trade_id
      delete data.reference_code;

      const formatDateField = (date) => {
        if (!date) return null;
        const d = date instanceof Date ? date : new Date(date);
        if (isNaN(d.getTime())) return null;
        const n = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 12, 0, 0);
        return n.toISOString().split("T")[0] + " 12:00:00Z";
      };

      const linkedId = linkedOption?.id || getLinkedOptionId(object);
      const terminationPre = object?.terminationPreOption
        || (typeof object?.linked_pre_option === "object" ? object.linked_pre_option : null);
      const isPartialPair = isPartialTerminationPair(object);
      const isCreateFromTermination = object?.derivative_action === "create" && linkedId != null;
      if (isCreateFromTermination) {
        // DRF exposes parent_option as PrimaryKeyRelatedField (writable);
        // parent_option_id is read-only, so we must use the FK field name.
        data.parent_option = linkedId;
      }

      setSubmitting(true)

      if (isPartialPair && (!linkedId || !linkedOption)) {
        setSubmitting(false);
        setErrors({
          general: t(
            "pre_option_original_missing",
            "Alkuperäistä sopimusta ei löytynyt. Päivitä sivu ja yritä uudelleen."
          ),
        });
        return;
      }

      // Check if we have a linked_option - if so, update existing option instead of creating new one
      const isEditingExisting = Boolean(linkedId) && object?.derivative_action !== "create";

      // Ensure data.id is a number, not an object
      if (isEditingExisting && typeof data.id === 'object' && data.id !== null) {
        data.id = data.id.id || data.id;
      }

      let httpPromise;
      if (isPartialPair) {
        const { originalId, originalPatch, terminatedCreate, remainingCreate } = buildPartialTerminationApproval({
          linkedOption,
          linkedId,
          remainingData: data,
          terminationPre,
          values,
          portfolioId: object?.portfolio || portfolio?.id,
          formatDateField,
        });
        const steps = [];
        if (terminatedCreate) steps.push(() => httpCreateOption(terminatedCreate));
        if (remainingCreate) steps.push(() => httpCreateOption(remainingCreate));
        steps.push(() => httpUpdateOption(originalId, originalPatch));
        httpPromise = steps.reduce((chain, step) => chain.then(step), Promise.resolve());
      } else {
        const httpCall = isEditingExisting ? httpUpdateOption : httpCreateOption;
        httpPromise = isEditingExisting ? httpCall(data.id, data) : httpCall(data);
      }
      
      httpPromise.then(res => {
        if(res?.data?.id){
          handleFileUpload([res?.data?.id], files)
        }
        const messageKey = isPartialPair
          ? "pre_option_partial_saved"
          : isEditingExisting ? "updated_message" : "created_message";
        const defaultMessage = isPartialPair
          ? "Vanha sopimus terminoitu ja uusi kirjattu"
          : isEditingExisting ? "Updated" : "Created";
        props.notify({ title:t("derivative_title","Derivative"), message:t(messageKey, defaultMessage)})

        const pre_update_data ={
            id: values?.original_id || object?.id,
            portfolio: values?.portfolio || object?.portfolio,
            processed: true,
            ...(linkedId ? { linked_option: linkedId } : {}),
        }
        const linkedPreOptionId = getPreOptionLinkId(object?.linked_pre_option)
          || object?.terminationPreOption?.id;
        
        if (!pre_update_data.id) {
          console.error("PreOption ID is missing! Cannot update PreOption.");
        } else {
          const markProcessed = httpUpdatePreOption(pre_update_data).then((res) => {
            if (!linkedPreOptionId || linkedPreOptionId === pre_update_data.id) return res;
            return httpUpdatePreOption({
              id: linkedPreOptionId,
              portfolio: values?.portfolio || object?.portfolio,
              processed: true,
              ...(linkedId ? { linked_option: linkedId } : {}),
            }).then(() => res, () => res);
          });
          markProcessed.then(res => {
            console.log("httpUpdatePreOption success", res)
            
            // AI messages (non-blocking, ei tarvitse odottaa)
            const ai_data = {
                name: "Correction",
                type: 2,
                role: "user",
                schema_model: "Option",
                content: values?.ai_comments,
                portfolio: portfolio?.id,
            }

            httpCreateFileAiMessages(ai_data).then(res => {
                console.log("httpCreateFileAiMessages", res)
            }, error => {
                console.log("httpCreateFileAiMessages error", error)
            })
            
            // Resolve only after PreOption is updated
            handleResolve()
          }, error => {
            console.log("httpUpdatePreOption error", error)
            // Resolve even on error to close modal
          })
        }
        
      }, error => {
        if (error?.data?.detail) {
          setErrors({
            general: error?.data?.detail,
          })
        }
        else if (error?.data) {
          setErrors({
            ...error?.data,
          })
        }
        else {
          setErrors({
            general: t("error_unknown","Unknown error. Please try again later or contact support.")
          })
        }
      }).finally(() => {
        setSubmitting(false)
      })
      
    },
    displayName: "BasicForm"
  
})(PreOptionEdit)
    
    
const PreOptionEditView = ({history, object, objectId, modalOpen, ...rest}) => {
  // API requests here
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [preSetValues, setPreSetValues] = useState(defaultValues);
  const { notify } = useNotification(); // import { useNotification } from "stories/components/Notification"
  const [stockMarket, setStockMarket] = useState("XHEL");
  const [portfolioSourceRate, setPortfolioSourceRate] = useState(1.0);
  const [securitySourceRateDate, setSecuritySourceRateDate] = useState(null);
  
  // Store: States
  const portfolios = useStoreState((state) => state.portfolio?.portfolios);
  const activePortfolio = useStoreState((state) => state.portfolio?.portfolio);
  // The document's bank account already picked this portfolio. Do not
  // substitute the portfolio that happens to be open in the header.
  const portfolio = object?.portfolio
    ? (portfolios?.find(p => p.id === object.portfolio) || null)
    : activePortfolio;
  const stocks = useStoreState((state) => state.stocks?.objects);
  const privateStocks = useStoreState((state) => state.privateStocks?.objects);
  const private_investment_funds = useStoreState((state) => state.privateInvestmentFunds?.objects);
  const account = useStoreState((state) => state.account?.account);
  const storedOptions = useStoreState((state) => state.options?.options);
  
  // Store: Actions
  const getBasicDataObjectsAction = useStoreActions((actions) => actions.stocks.getObjects);
  
  const [files, setFiles] = useState([]);
  const [trxFiles, setTrxFiles] = useState([]);
  const [linkedOption, setLinkedOption] = useState(null);
  const [portfolioOptions, setPortfolioOptions] = useState([]);
  const [portfolioOptionsLoaded, setPortfolioOptionsLoaded] = useState(false);
  const manualLinkRef = useRef(false);

  const handleLinkedOptionChange = (option) => {
    manualLinkRef.current = true;
    setLinkedOption(option);
  };

  useEffect(() => {
    manualLinkRef.current = false;
    setPortfolioOptions([]);
    setPortfolioOptionsLoaded(false);
  }, [object?.id]);

  useEffect(() => {
    // The open-contract list is also needed when a confirmation is already
    // linked, so the reviewer can switch the contract being terminated.
    if (!object?.portfolio) return undefined;
    let cancelled = false;
    httpGetOptions({ portfolio: object.portfolio }).then((res) => {
      if (cancelled) return;
      setPortfolioOptions(extractOptionsList(res?.data));
      setPortfolioOptionsLoaded(true);
    }).catch(() => {
      if (cancelled) return;
      setPortfolioOptions([]);
      setPortfolioOptionsLoaded(true);
    });
    return () => {
      cancelled = true;
    };
  }, [object]);

  useEffect(() => {
    let cancelled = false;

    const apply = (option) => {
      if (!cancelled && option && !manualLinkRef.current) {
        setLinkedOption(option);
      }
    };

    const resolveOriginalOption = async () => {
      if (!object) {
        setLinkedOption(null);
        return;
      }
      if (manualLinkRef.current) return;

      if (object.linked_option_data?.id) {
        apply(object.linked_option_data);
      } else if (
        object.linked_option &&
        typeof object.linked_option === "object" &&
        object.linked_option.id
      ) {
        apply(object.linked_option);
      }

      const linkedId = getLinkedOptionId(object);
      const tradeCodes = [object.old_reference_code, object.reference_code].filter(Boolean);
      const knownOptions = [...(storedOptions || []), ...(portfolioOptions || [])];
      const fromStore = linkedId
        ? knownOptions.find((opt) => Number(opt.id) === Number(linkedId))
        : findOptionForReferences(knownOptions, tradeCodes);
      if (fromStore) {
        apply(fromStore);
      }

      if (linkedId) {
        try {
          const res = await httpGetOption(linkedId);
          if (res?.data) {
            apply(res.data);
            return;
          }
        } catch (error) {
          // Fall through to trade_id lookup when the linked option GET fails.
        }
      }

      if (fromStore && (linkedId || portfolioOptionsLoaded)) return;

      if (!linkedId && portfolioOptionsLoaded && !cancelled && !manualLinkRef.current) {
        setLinkedOption(null);
      }
    };

    resolveOriginalOption();

    return () => {
      cancelled = true;
    };
  }, [object, storedOptions, portfolioOptions, portfolioOptionsLoaded]);

  useEffect(() => {
    if (portfolio) {
      setPreSetValues(s => ({
        ...s,
        currency_1: portfolio?.currency_label
      }))
    }
  }, [portfolio])

  const handleFileUpload = (options) => {
    if (!files?.length) return
    const formData = new FormData();
    files.forEach(file => formData.append('file', file))
    formData.append('account', account?.id);
    formData.append('options', options)
    formData.append('portfolio', object?.portfolio);
    
    httpUploadFiles(formData).then(res => {
      if (res?.data){
        console.log(res?.data)
      }
      setFiles([])
    }, error => {
      if(error?.data?.message) {
        // setErrorMessage(t(error?.data?.message))
      }
      if(error?.data?.general) {
        const general = error?.data?.general?.toString()
        const formattedError = general.replace(/:/g, '.')
        // setErrorMessage(t(formattedError))
      }
    }).finally(() => {
      // setLoadingSendExcel(false)
    })
  };

  const getObject = (id) => {
    setLoading(true)
    
    httpGetOption(id).then(res => {
      const legacySettlement = resolveLegacyTerminationSettlement(res?.data);
      setPreSetValues({
        ...defaultValues,
        ...res?.data,
        start_date: res?.data?.start_date ? moment(res?.data?.start_date).toDate() : null,
        end_date: res?.data?.end_date ? moment(res?.data?.end_date).toDate() : null,
        exercise_date: res?.data?.exercise_date ? moment(res?.data?.exercise_date).toDate() : null,
        option_state_date: res?.data?.option_state_date ? moment(res?.data?.option_state_date).toDate() : null,
        premium: padDerivativePrice(res?.data?.premium),
        premium_total: padDerivativePrice(res?.data?.premium_total),
        other_expenses: padDerivativePrice(res?.data?.other_expenses),
        strike_price: padDerivativePrice(res?.data?.strike_price),
        exercise_price: padDerivativePrice(res?.data?.exercise_price),
        termination_price: padDerivativePrice(legacySettlement.termination_price),
        receives: padDerivativePrice(legacySettlement.receives),
        termination_fee: legacySettlement.termination_price || res?.data?.termination_fee || 0,
      });
    
    }).finally(response => {
      setLoading(false);
    })
  }
  
  useEffect(() => {
    if (objectId && parseInt(objectId) > -1) {
      getObject(objectId)
    }
    if (object) {
        getBasicDataObjectsAction({related_model_type: object?.related_model_type, id:object?.related_model_id, show_hidden: true})
        setTrxFiles(object.file ? [object.file] : []);
        
        // Apply currency conversion using exchange rate or default to 1
        const exchangeRate = object?.currency_exchange_rate || 1.0;
        const convertedPremium = object?.premium ? roundScaleDecimals(object?.premium * exchangeRate, PRICE_DECIMAL_PLACES) : object?.premium;
        const convertedPremiumTotal = object?.premium_total ? roundScaleDecimals(object?.premium_total * exchangeRate, PRICE_DECIMAL_PLACES) : object?.premium_total;
        const convertedOtherExpenses = object?.other_expenses ? roundScaleDecimals(object?.other_expenses * exchangeRate, PRICE_DECIMAL_PLACES) : object?.other_expenses;

        const finalAssetName = object?.asset_name || object?.related_model?.name || "";
        
        // If PreOption has linked_option, use its id for editing existing option
        // BUT only if derivative_action is not "create" (create means creating a new option)
        const effectiveId = object?.derivative_action !== "create"
          ? getLinkedOptionId(object)
          : null;
        
        console.log("PreOption object.linked_option:", object?.linked_option, "derivative_action:", object?.derivative_action, "effectiveId:", effectiveId);

        const terminationPre = object?.terminationPreOption
          || (typeof object?.linked_pre_option === "object" ? object.linked_pre_option : null);
        const terminationUnitPrice = terminationPre?.termination_price ?? object?.termination_price;
        const terminationCash = terminationPre?.receives ?? object?.receives;
        const storedFee = terminationPre?.termination_fee ?? object?.termination_fee;
        const terminationFee = (terminationUnitPrice != null && Number(terminationUnitPrice) !== 0)
          ? terminationUnitPrice
          : (storedFee ?? terminationUnitPrice ?? 0);
        const remainingStartDate = object?.start_date;
        const remainingStartPolygon = object?.start_date_polygon;
        const remainingStartPage = object?.start_date_page;
        
        setPreSetValues({
            ...defaultValues,
            ...object,
            id: effectiveId, // Set id to linked_option if it exists
            original_id: object?.id,
            broker_name: object?.broker_name,
            start_date: remainingStartDate ? moment(remainingStartDate).toDate() : new Date(),
            end_date: object?.end_date ? moment(object?.end_date).toDate() : new Date(),
            option_state: object?.option_state,
            option_state_date: object?.option_state_date ? moment(object?.option_state_date).toDate() : null,
            premium: padDerivativePrice(convertedPremium),
            premium_total: padDerivativePrice(convertedPremiumTotal),
            other_expenses: padDerivativePrice(convertedOtherExpenses),
            lot_size: object?.lot_size,
            lot_amount: object?.lot_amount,
            quantity: object?.quantity,
            strike_price: padDerivativePrice(object?.strike_price),
            exercise_price: padDerivativePrice(object?.exercise_price),
            notes: object?.notes,
            reference_code: object?.reference_code,
            currency_1: object?.currency_1,
            currency_2: object?.currency_2,
            asset_type: object?.asset_type,
            asset_name: finalAssetName,
            derivative_type: object?.derivative_type,
            option_right: object?.option_right,
            role: object?.role,
            related_model_id: object?.related_model_id,
            related_model_type: object?.related_model_type,
            asset_id: object?.related_model_id,
            asset_stock: {id: object?.related_model_id, value: object?.related_model_id, label: object?.related_model?.name, ticker_symbol: object?.related_model?.ticker_symbol, stock_market: object?.related_model?.stock_market, currency_label: object?.related_model?.currency_label},
            currency_exchange_rate: exchangeRate,
            asset_name_polygon: object?.asset_name_polygon,
            strike_price_polygon: object?.strike_price_polygon,
            premium_polygon: object?.premium_polygon,
            premium_total_polygon: object?.premium_total_polygon,
            other_expenses_polygon: object?.other_expenses_polygon,
            start_date_polygon: remainingStartPolygon,
            end_date_polygon: object?.end_date_polygon,
            lot_size_polygon: object?.lot_size_polygon,
            lot_amount_polygon: object?.lot_amount_polygon,
            broker_polygon: object?.broker_polygon,
            reference_code_polygon: object?.reference_code_polygon,
            owner_name: object?.owner_name,
            writer_name: object?.writer_name,
            owner_name_polygon: object?.owner_name_polygon,
            writer_name_polygon: object?.writer_name_polygon,
            option_state_date_polygon: object?.option_state_date_polygon,
            termination_price: padDerivativePrice(terminationUnitPrice),
            receives: padDerivativePrice(terminationCash ?? 0),
            termination_fee: terminationFee ?? 0,
            termination_price_polygon: terminationPre?.termination_price_polygon ?? object?.termination_price_polygon,
            asset_name_page: object?.asset_name_page,
            strike_price_page: object?.strike_price_page,
            premium_page: object?.premium_page,
            premium_total_page: object?.premium_total_page,
            other_expenses_page: object?.other_expenses_page,
            start_date_page: remainingStartPage,
            end_date_page: object?.end_date_page,
            lot_size_page: object?.lot_size_page,
            lot_amount_page: object?.lot_amount_page,
            broker_page: object?.broker_page,
            reference_code_page: object?.reference_code_page,
            owner_name_page: object?.owner_name_page,
            writer_name_page: object?.writer_name_page,
            option_state_date_page: object?.option_state_date_page,
            termination_price_page: terminationPre?.termination_price_page ?? object?.termination_price_page,
        });
    }
  }, [objectId, modalOpen])
  
  if (loading) {
    return (
      <Container fluid className="d-flex justify-content-center align-items-center">
        <Spinner className="mt-3" />
      </Container>
    )
  }

  return (
    <PreOptionEditFormik 
      t={t} 
      history={history} 
      preSetValues={preSetValues} 
      notify={notify} 
      objectId={objectId} 
      portfolio={portfolio}
      portfolios={portfolios}
      stockMarket={stockMarket}
      setStockMarket={setStockMarket}
      getBasicDataObjects={getBasicDataObjectsAction}
      portfolioSourceRate={portfolioSourceRate}
      setPortfolioSourceRate={setPortfolioSourceRate}
      securitySourceRateDate={securitySourceRateDate}
      setSecuritySourceRateDate={setSecuritySourceRateDate}
      stocks={stocks}
      setFiles={setFiles}
      files={files}
      trxFiles={trxFiles}
      setTrxFiles={setTrxFiles}
      handleFileUpload={handleFileUpload}
      object={object}
      handleResolve={rest.handleResolve}
      {...rest}
      linkedOption={linkedOption}
      linkedOptionStockMarket={object?.related_model?.stock_market || linkedOption?.stock_market}
      portfolioOptions={portfolioOptions}
      onLinkedOptionChange={handleLinkedOptionChange}
    />
  )
}
  

export default PreOptionEditView;
